import React, { useEffect, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import TpDialog from '../shared/tp_dialog';
import WhyPopover from './why_popover';
import { fetch_drilldown } from '../api';
import { fmt, is_permission_error } from '../format';
import { collect_session_statuses, session_status_options } from './drilldown_logic';
import { drilldown_view } from './adapters';
import {
  DRILLDOWN_CATEGORY_ADDITIONAL,
  DRILLDOWN_KIND_ADDITIONAL,
  drilldown_params
} from './params';

/*
 * PROD-7828 R5 — drill-down for one L2 category: item and penalty rows as the backend sends them,
 * grouped by IST day. Read-only: session edits happen on the Hridaya session details page and the
 * engine recalculates. The backend's show_payment_col hides Payment for trials (base pay is ₹0).
 * The status filter is applied server-side to items AND penalties, each on its own status column.
 * The ADDITIONAL category lists additional-payment entries instead (Q23): their own columns, no
 * session status, amounts signed.
 */
const EMPTY_VIEW = { days: [], show_payment_col: true, count: 0 };

const ADDITIONAL_COLUMNS = [
  { label: 'Date / time (IST)' },
  { label: 'Category' },
  { label: 'Source' },
  { label: 'Ref' },
  { label: 'Session' },
  { label: 'Amount', right: true },
  { label: 'Note' },
  { label: 'Added by' }
];

const SessionDrilldownModal = ({
  show,
  close,
  pay_month,
  month_label,
  teacher_uuid,
  category,
  category_label,
  kind,
  on_error
}) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(EMPTY_VIEW);
  const [statuses, setStatuses] = useState([]);
  const [status_filter, setStatusFilter] = useState('');
  // A failed load is not an empty category: it shows its own error, and attempt drives Try again.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // A new category/kind starts unfiltered so the status options cover the whole category.
  useEffect(() => {
    setStatusFilter('');
    setStatuses([]);
  }, [teacher_uuid, pay_month, category, kind]);

  useEffect(() => {
    if (!show || !category || !teacher_uuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch_drilldown(
      drilldown_params({ teacher_uuid, pay_month, category, kind, session_status: status_filter })
    )
      .then((data) => {
        if (cancelled) return;
        const next = drilldown_view(data);
        setView(next);
        if (!status_filter) setStatuses(collect_session_statuses(next.days));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (is_permission_error(e)) on_error(e, 'Failed to load the session drill-down.');
        setView(EMPTY_VIEW);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, teacher_uuid, pay_month, category, kind, status_filter, attempt, on_error]);

  // The framing and the columns follow the request (known before, and without, a response).
  const entries = category === DRILLDOWN_CATEGORY_ADDITIONAL;
  const show_payment = view.show_payment_col;
  const item_cols = show_payment ? 8 : 7;
  const col_count = entries ? ADDITIONAL_COLUMNS.length : item_cols;

  const amount_cell = (value, tone, explain) =>
    value === null ? (
      '—'
    ) : (
      <WhyPopover detail={explain} amount_node={<span className={tone}>{fmt(value, 2)}</span>} />
    );

  // R6: a penalty row's own breakdown — original/corrected/waived/free pass from money.* (CD-194: a pass
  // isn't a waiver, so waived never reads waiver_status). An orphan was never charged: money.uncharged_orphan
  // stands in place of the amount, not beside it.
  const penalty_cell = (r) => {
    if (r.is_orphaned) {
      return (
        <WhyPopover
          detail={r.explain}
          amount_node={
            <span className="tp-neg">
              Orphaned - not charged ({fmt(r.uncharged_orphan_amount, 2)})
            </span>
          }
        />
      );
    }
    const has_correction = r.corrected_amount !== null;
    return (
      <WhyPopover
        detail={r.explain}
        amount_node={
          <span>
            <span
              className="tp-neg"
              style={has_correction ? { textDecoration: 'line-through' } : undefined}
            >
              {fmt(r.original_amount, 2)}
            </span>
            {has_correction ? (
              <>
                {' → '}
                <span className="tp-neg">{fmt(r.corrected_amount, 2)}</span>
              </>
            ) : null}
            {r.waived_amount > 0 ? (
              <div className="tp-pos" style={{ fontSize: 10 }}>
                Waived {fmt(r.waived_amount, 2)}
              </div>
            ) : null}
            {r.free_pass_amount > 0 ? (
              <div className="tp-pos" style={{ fontSize: 10 }}>
                Free pass {fmt(r.free_pass_amount, 2)}
              </div>
            ) : null}
          </span>
        }
      />
    );
  };

  const session_link = (r) =>
    r.session_uuid ? (
      <a
        className="tp-link"
        href={`/sessiondetails?id=${r.session_uuid}`}
        target="_blank"
        rel="noopener noreferrer"
        title={r.session_uuid}
      >
        {r.session_short}
      </a>
    ) : (
      '—'
    );

  const additional_row = (r) => (
    <tr key={r.key}>
      <td>{r.date_text}</td>
      <td>{r.category_label}</td>
      <td>{r.source_text}</td>
      <td className="tp-mono">{r.reference_id || '—'}</td>
      <td className="tp-mono">{session_link(r)}</td>
      <td style={{ textAlign: 'right' }}>
        <WhyPopover
          detail={r.explain}
          amount_node={<span className={r.amount_tone}>{r.amount_text}</span>}
        />
      </td>
      <td style={{ fontSize: 11 }} title={r.note || undefined}>
        {r.note || '—'}
      </td>
      <td style={{ fontSize: 11 }} title={r.added_by_title || undefined}>
        {r.added_by}
      </td>
    </tr>
  );

  if (!show) return null;

  return (
    <TpDialog title_id="tp-drilldown-title" on_close={close}>
      <div className="tp-modal tp-modal-wide">
        <h4 id="tp-drilldown-title">
          {entries
            ? `Additional payments · ${month_label}`
            : `Session details — ${category_label || category} · ${month_label}`}
        </h4>
        <div className="tp-ctx">
          {entries
            ? 'Read-only. Entries are added, edited and removed on the Additional payments tab; the payment recalculates from there.'
            : 'Read-only. Session-level corrections are made on the Hridaya session details page; the payment recalculates from there.'}
        </div>

        <div className="tp-frow">
          {entries ? null : (
            <div style={{ width: '220px' }}>
              <CustomSingleSelect
                label="Session status"
                options={session_status_options(statuses)}
                value={status_filter}
                defaultOption="All statuses"
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
          )}
          {failed ? null : <span className="tp-card-hd-sub">{view.count} rows</span>}
        </div>

        {loading ? (
          <CustomLoading />
        ) : (
          <div className="tp-tablewrap">
            <table className="tp-table">
              <thead>
                {entries ? (
                  <tr>
                    {ADDITIONAL_COLUMNS.map((c) => (
                      <th key={c.label} style={c.right ? { textAlign: 'right' } : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                ) : (
                  <tr>
                    <th>Time (IST)</th>
                    <th>Session</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Duration</th>
                    {show_payment ? <th style={{ textAlign: 'right' }}>Payment</th> : null}
                    <th style={{ textAlign: 'right' }}>Incentive</th>
                    <th style={{ textAlign: 'right' }}>Penalty</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {failed ? (
                  <tr>
                    <td colSpan={col_count}>
                      <div className="tp-banner tp-banner-error" role="alert">
                        {entries ? 'Could not load the entries.' : 'Could not load the sessions.'}{' '}
                        <button
                          type="button"
                          className="tp-link"
                          onClick={() => setAttempt((a) => a + 1)}
                        >
                          Try again
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {!failed && view.days.length === 0 ? (
                  <tr>
                    <td colSpan={col_count} className="tp-empty">
                      {entries
                        ? 'No additional payments for this teacher and month.'
                        : 'No rows for this category and status.'}
                    </td>
                  </tr>
                ) : null}
                {view.days.map((day) => (
                  <React.Fragment key={day.key}>
                    <tr>
                      <td colSpan={col_count} className="tp-daygroup">
                        {day.label} ({day.rows.length} {day.rows.length === 1 ? 'row' : 'rows'})
                      </td>
                    </tr>
                    {day.rows.map((r) =>
                      r.kind === DRILLDOWN_KIND_ADDITIONAL ? (
                        additional_row(r)
                      ) : (
                        <tr key={r.key}>
                          <td>{r.time_label}</td>
                          <td className="tp-mono">{session_link(r)}</td>
                          <td>{r.status_label}</td>
                          <td style={{ fontSize: 11 }} title={r.note || undefined}>
                            {r.type_label}
                            {r.note ? ` — ${r.note}` : ''}
                          </td>
                          <td>{r.duration_label}</td>
                          {show_payment ? (
                            <td style={{ textAlign: 'right' }}>
                              {amount_cell(r.payment, 'tp-pos', r.explain)}
                            </td>
                          ) : null}
                          <td style={{ textAlign: 'right' }}>
                            {amount_cell(r.incentive, 'tp-pos', r.explain)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {r.kind === 'penalty'
                              ? penalty_cell(r)
                              : amount_cell(r.penalty, 'tp-neg', r.explain)}
                          </td>
                        </tr>
                      )
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={close}>
            Close
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

export default SessionDrilldownModal;
